import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { intakeAPI, aiAPI, encounterAPI, documentAPI, labAPI } from '../../lib/api';
import { 
  Robot, 
  Heart,
  Thermometer,
  Pulse,
  Wind,
  Warning,
  Check,
  Sparkle,
  Ruler,
  Scales,
  Flask,
  FileText,
  File,
  Image,
  FilePdf,
  DownloadSimple,
  Eye,
  ArrowSquareOut
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function TriagePage() {
  const { intakeId } = useParams();
  const navigate = useNavigate();
  const [intake, setIntake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAssessment, setAiAssessment] = useState(null);
  const [error, setError] = useState('');
  
  const [vitalSigns, setVitalSigns] = useState({
    blood_pressure: '',
    heart_rate: '',
    temperature: '',
    respiratory_rate: '',
    oxygen_saturation: '',
    weight: '',
    height: '',
  });
  
  const [triageNotes, setTriageNotes] = useState('');
  const [labs, setLabs] = useState([]);
  const [medicalDocs, setMedicalDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const loadIntake = useCallback(async () => {
    try {
      const res = await intakeAPI.getById(intakeId);
      setIntake(res.data);
      if (res.data.vital_signs) {
        setVitalSigns(res.data.vital_signs);
      }
      if (res.data.triage_notes) {
        setTriageNotes(res.data.triage_notes);
      }
      if (res.data.ai_risk_assessment) {
        setAiAssessment(res.data.ai_risk_assessment);
      }
      
      // Load labs and documents for this patient
      if (res.data.patient_id) {
        loadPatientDocs(res.data.patient_id);
      }
    } catch (err) {
      setError('Failed to load intake');
    } finally {
      setLoading(false);
    }
  }, [intakeId]);

  const loadPatientDocs = async (patientId) => {
    setLoadingDocs(true);
    try {
      const [docsRes, labsRes] = await Promise.all([
        documentAPI.getPatientDocs(patientId, true).catch(() => ({ data: [] })),
        labAPI.getPatientLabs(patientId).catch(() => ({ data: [] }))
      ]);
      
      // Filter for medical documents (X-rays, MRIs, lab results, etc.)
      const docs = docsRes.data || [];
      const medDocs = docs.filter(d => 
        ['xray', 'mri', 'lab_result', 'medical_image', 'other'].includes(d.document_type)
      );
      setMedicalDocs(medDocs);
      setLabs(labsRes.data || []);
    } catch (err) {
      console.error('Error loading patient documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    loadIntake();
  }, [loadIntake]);

  const runAiTriage = async () => {
    setAiLoading(true);
    try {
      const res = await aiAPI.triage({
        symptoms: intake.symptoms,
        chief_complaint: intake.chief_complaint,
        pain_level: intake.pain_level,
        vital_signs: vitalSigns,
        medical_history: intake.medical_history,
      });
      setAiAssessment(res.data.assessment);
    } catch (err) {
      setError('AI assessment failed. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const saveTriage = async (status = 'in_triage') => {
    setSaving(true);
    setError('');
    try {
      // Convert vital signs to proper types
      const formattedVitals = {
        blood_pressure: vitalSigns.blood_pressure || null,
        heart_rate: vitalSigns.heart_rate ? parseInt(vitalSigns.heart_rate) : null,
        temperature: vitalSigns.temperature ? parseFloat(vitalSigns.temperature) : null,
        respiratory_rate: vitalSigns.respiratory_rate ? parseInt(vitalSigns.respiratory_rate) : null,
        oxygen_saturation: vitalSigns.oxygen_saturation ? parseInt(vitalSigns.oxygen_saturation) : null,
        weight: vitalSigns.weight ? parseFloat(vitalSigns.weight) : null,
        height: vitalSigns.height ? parseFloat(vitalSigns.height) : null,
      };
      
      await intakeAPI.updateTriage(intakeId, {
        vital_signs: formattedVitals,
        triage_notes: triageNotes,
        status,
      });
      
      if (status === 'ready_for_provider') {
        // Create encounter
        await encounterAPI.create(intakeId);
        toast.success('Patient ready for provider!');
        navigate('/cma');
      } else {
        toast.success('Progress saved successfully!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save triage';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to save triage');
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to save triage');
    } finally {
      setSaving(false);
    }
  };

  // Calculate BMI from height (inches) and weight (lbs)
  const calculateBMI = () => {
    const weightLbs = parseFloat(vitalSigns.weight);
    const heightIn = parseFloat(vitalSigns.height);
    if (weightLbs && heightIn && heightIn > 0) {
      const bmi = (weightLbs / (heightIn * heightIn)) * 703;
      return bmi.toFixed(1);
    }
    return null;
  };

  const getBMICategory = (bmi) => {
    if (!bmi) return null;
    const bmiValue = parseFloat(bmi);
    if (bmiValue < 18.5) return { label: 'Underweight', color: 'text-blue-600 bg-blue-50' };
    if (bmiValue < 25) return { label: 'Normal', color: 'text-green-600 bg-green-50' };
    if (bmiValue < 30) return { label: 'Overweight', color: 'text-amber-600 bg-amber-50' };
    return { label: 'Obese', color: 'text-red-600 bg-red-50' };
  };

  const bmi = calculateBMI();
  const bmiCategory = getBMICategory(bmi);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!intake) {
    return (
      <div className="p-8 text-center">
        <Warning size={48} className="text-red-500 mx-auto mb-4" />
        <p className="text-slate-700">Intake not found</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Patient Triage
        </h1>
        <p className="text-slate-500 mt-1">Complete triage assessment for this patient</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Patient Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Chief Complaint */}
          <div className="card-clinical p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Patient Complaint</h2>
            <p className="text-slate-700 mb-4">{intake.chief_complaint}</p>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Symptoms:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {intake.symptoms.map((s) => (
                    <span key={`symptom-${s}`} className="px-2 py-0.5 bg-slate-100 rounded text-slate-700">{s}</span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-slate-500">Duration:</span>
                <p className="text-slate-900 font-medium">{intake.symptom_duration}</p>
              </div>
              <div>
                <span className="text-slate-500">Pain Level:</span>
                <p className="text-slate-900 font-medium">{intake.pain_level}/10</p>
              </div>
              <div>
                <span className="text-slate-500">Allergies:</span>
                <p className="text-slate-900">{intake.allergies?.join(', ') || 'None reported'}</p>
              </div>
            </div>
          </div>

          {/* Vital Signs */}
          <div className="card-clinical p-6" data-testid="vital-signs-form">
            <h2 className="font-semibold text-slate-900 mb-4">Record Vital Signs</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Heart size={16} className="text-red-500" /> Blood Pressure
                </label>
                <input
                  type="text"
                  value={vitalSigns.blood_pressure}
                  onChange={(e) => setVitalSigns({ ...vitalSigns, blood_pressure: e.target.value })}
                  className="input-clinical"
                  placeholder="120/80"
                  data-testid="vital-bp"
                />
              </div>
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Pulse size={16} className="text-blue-500" /> Heart Rate
                </label>
                <input
                  type="number"
                  value={vitalSigns.heart_rate}
                  onChange={(e) => setVitalSigns({ ...vitalSigns, heart_rate: e.target.value })}
                  className="input-clinical"
                  placeholder="72"
                  data-testid="vital-hr"
                />
              </div>
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Thermometer size={16} className="text-amber-500" /> Temperature
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={vitalSigns.temperature}
                  onChange={(e) => setVitalSigns({ ...vitalSigns, temperature: e.target.value })}
                  className="input-clinical"
                  placeholder="98.6"
                  data-testid="vital-temp"
                />
              </div>
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Wind size={16} className="text-teal-500" /> O2 Saturation
                </label>
                <input
                  type="number"
                  value={vitalSigns.oxygen_saturation}
                  onChange={(e) => setVitalSigns({ ...vitalSigns, oxygen_saturation: e.target.value })}
                  className="input-clinical"
                  placeholder="98"
                  data-testid="vital-o2"
                />
              </div>
            </div>

            {/* Height, Weight, BMI Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-200">
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Ruler size={16} className="text-purple-500" /> Height (inches)
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={vitalSigns.height}
                  onChange={(e) => setVitalSigns({ ...vitalSigns, height: e.target.value })}
                  className="input-clinical"
                  placeholder="68"
                  data-testid="vital-height"
                />
              </div>
              <div>
                <label className="label-clinical flex items-center gap-2">
                  <Scales size={16} className="text-indigo-500" /> Weight (lbs)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={vitalSigns.weight}
                  onChange={(e) => setVitalSigns({ ...vitalSigns, weight: e.target.value })}
                  className="input-clinical"
                  placeholder="150"
                  data-testid="vital-weight"
                />
              </div>
              <div className="col-span-2">
                <label className="label-clinical flex items-center gap-2">
                  BMI (Auto-calculated)
                </label>
                <div className={`input-clinical flex items-center justify-between ${bmiCategory?.color || 'bg-slate-50'}`}>
                  <span className="font-semibold text-lg">{bmi || '--'}</span>
                  {bmiCategory && (
                    <span className={`text-sm font-medium px-2 py-1 rounded-full ${bmiCategory.color}`}>
                      {bmiCategory.label}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Triage Notes */}
          <div className="card-clinical p-6">
            <h2 className="font-semibold text-slate-900 mb-4">Triage Notes</h2>
            <textarea
              value={triageNotes}
              onChange={(e) => setTriageNotes(e.target.value)}
              className="input-clinical min-h-[120px]"
              placeholder="Enter triage observations and notes..."
              data-testid="triage-notes"
            />
          </div>

          {/* Labs & Documents Review */}
          <div className="card-clinical p-6">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Flask size={20} className="text-indigo-600" />
              Labs & Documents Review
            </h2>
            
            {loadingDocs ? (
              <div className="text-center py-4">
                <div className="spinner mx-auto mb-2" />
                <p className="text-sm text-slate-500">Loading documents...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Lab Results */}
                {labs.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-2">Lab Results</h3>
                    <div className="space-y-2">
                      {labs.map((lab, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                          <div className="flex items-center gap-3">
                            <FileText size={18} className="text-indigo-600" />
                            <div>
                              <p className="text-sm font-medium text-slate-800">{lab.test_name || 'Lab Result'}</p>
                              <p className="text-xs text-slate-500">
                                {new Date(lab.created_at).toLocaleDateString()} 
                                {lab.status && <span className="ml-2 capitalize">• {lab.status}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => navigate(`/staff/labs/${lab.id}`)}
                              className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg"
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Uploaded Medical Documents */}
                {medicalDocs.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-700 mb-2">Uploaded Documents</h3>
                    <div className="space-y-2">
                      {medicalDocs.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="flex items-center gap-3">
                            {doc.mime_type?.includes('image') ? (
                              <Image size={18} className="text-green-600" />
                            ) : doc.mime_type?.includes('pdf') ? (
                              <FilePdf size={18} className="text-red-600" />
                            ) : (
                              <File size={18} className="text-slate-600" />
                            )}
                            <div>
                              <p className="text-sm font-medium text-slate-800 capitalize">
                                {doc.document_type?.replace(/_/g, ' ') || 'Document'}
                              </p>
                              <p className="text-xs text-slate-500">
                                {doc.file_name} • {(doc.file_size / 1024).toFixed(1)}KB
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {doc.mime_type?.includes('image') && (
                              <button 
                                onClick={() => setSelectedDoc(doc)}
                                className="p-2 text-teal-600 hover:bg-teal-100 rounded-lg"
                                title="Preview"
                              >
                                <Eye size={16} />
                              </button>
                            )}
                            <a 
                              href={`data:${doc.mime_type};base64,${doc.file_data}`}
                              download={doc.file_name}
                              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                              title="Download"
                            >
                              <DownloadSimple size={16} />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {labs.length === 0 && medicalDocs.length === 0 && (
                  <div className="text-center py-6 text-slate-500">
                    <Flask size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No labs or documents uploaded by patient</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Document Preview Modal */}
          {selectedDoc && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedDoc(null)}>
              <div className="bg-white rounded-xl p-4 max-w-3xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900 capitalize">
                    {selectedDoc.document_type?.replace(/_/g, ' ')}
                  </h3>
                  <button onClick={() => setSelectedDoc(null)} className="text-slate-500 hover:text-slate-700">
                    ✕
                  </button>
                </div>
                <img 
                  src={`data:${selectedDoc.mime_type};base64,${selectedDoc.file_data}`}
                  alt={selectedDoc.file_name}
                  className="max-w-full rounded-lg"
                />
              </div>
            </div>
          )}
        </div>

        {/* AI Assistant */}
        <div className="space-y-6">
          {/* AI Triage */}
          <div className="card-clinical p-6 ai-feature-card" data-testid="ai-triage-panel">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Robot size={20} className="text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">AI Risk Assessment</h3>
                <p className="text-xs text-slate-500">Powered by GPT-5.2</p>
              </div>
            </div>
            
            <button
              onClick={runAiTriage}
              disabled={aiLoading}
              className="w-full btn-teal mb-4 flex items-center justify-center gap-2"
              data-testid="run-ai-triage"
            >
              {aiLoading ? (
                <>
                  <div className="spinner" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkle size={18} weight="fill" />
                  Run AI Assessment
                </>
              )}
            </button>
            
            {aiAssessment && (
              <div className="space-y-3 text-sm">
                {aiAssessment.urgency_level && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">Urgency Level</div>
                    <div className={`font-semibold ${
                      aiAssessment.urgency_level >= 4 ? 'text-red-600' :
                      aiAssessment.urgency_level >= 3 ? 'text-amber-600' : 'text-green-600'
                    }`}>
                      {aiAssessment.urgency_level}/5
                    </div>
                  </div>
                )}
                
                {aiAssessment.potential_conditions && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="text-slate-500 text-xs uppercase tracking-wider mb-1">Consider</div>
                    <ul className="text-slate-700 space-y-1">
                      {(Array.isArray(aiAssessment.potential_conditions) 
                        ? aiAssessment.potential_conditions 
                        : [aiAssessment.potential_conditions]
                      ).slice(0, 3).map((c, idx) => (
                        <li key={`cond-${typeof c === 'object' ? (c.condition || c.name || idx) : c}`}>• {typeof c === 'object' ? (c.condition || c.name || JSON.stringify(c)) : c}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {aiAssessment.care_gaps && aiAssessment.care_gaps.length > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="text-amber-700 text-xs uppercase tracking-wider mb-1 flex items-center gap-1">
                      <Warning size={14} /> Care Gaps
                    </div>
                    <ul className="text-amber-800 space-y-1">
                      {aiAssessment.care_gaps.slice(0, 3).map((g, idx) => (
                        <li key={`gap-${typeof g === 'object' ? (g.condition || g.gap || g.name || idx) : g}`}>• {typeof g === 'object' ? (g.condition || g.gap || g.name || JSON.stringify(g)) : g}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {aiAssessment.raw_assessment && (
                  <div className="p-3 bg-slate-50 rounded-lg text-slate-700 text-xs leading-relaxed max-h-48 overflow-y-auto">
                    {aiAssessment.raw_assessment}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="card-clinical p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => saveTriage('in_triage')}
                disabled={saving}
                className="w-full btn-outline flex items-center justify-center gap-2"
                data-testid="save-triage"
              >
                {saving ? <div className="spinner" /> : 'Save Progress'}
              </button>
              <button
                onClick={() => saveTriage('ready_for_provider')}
                disabled={saving}
                className="w-full btn-teal flex items-center justify-center gap-2"
                data-testid="complete-triage"
              >
                <Check size={18} weight="bold" />
                Ready for Provider
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
